/**
 * Digitisation queue (Rx-12): the filter controls → the API query, and how a row's people and
 * times read. Pure, so the rules are unit-tested (tests/unit/uploadQueue.test.mjs).
 */

import type { Upload, UploadPerson, UploadQueueQuery } from "../api/pharmacist";
import { localDigits } from "./phone";
import { dateRange } from "./queueSearch";

export type QueueStatus = "todo" | "pending_review" | "in_review" | "digitized" | "rejected" | "all";
export type QueueReviewer = "" | "me" | "unassigned";
export type QueueForWhom = "" | "self" | "family";
export type QueueSort = "oldest" | "newest" | "patient";

export interface QueueFilters {
  q: string;
  status: QueueStatus;
  reviewer: QueueReviewer;
  forWhom: QueueForWhom;
  from: string; // local calendar date yyyy-mm-dd, inclusive ("" = open)
  to: string; // local calendar date yyyy-mm-dd, inclusive ("" = open)
  sort: QueueSort;
  perPage: number;
}

export const PER_PAGE_OPTIONS = [25, 50, 100] as const;

export const STATUS_OPTIONS: { value: QueueStatus; label: string }[] = [
  { value: "todo", label: "To do" },
  { value: "pending_review", label: "Pending" },
  { value: "in_review", label: "In review" },
  { value: "digitized", label: "Digitised" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

export const REVIEWER_OPTIONS: { value: QueueReviewer; label: string }[] = [
  { value: "", label: "Anyone" },
  { value: "me", label: "Me" },
  { value: "unassigned", label: "Nobody yet" },
];

export const FOR_OPTIONS: { value: QueueForWhom; label: string }[] = [
  { value: "", label: "Anyone" },
  { value: "self", label: "Account holder" },
  { value: "family", label: "Family member" },
];

export const SORT_OPTIONS: { value: QueueSort; label: string }[] = [
  { value: "oldest", label: "Waiting longest" },
  { value: "newest", label: "Newest first" },
  { value: "patient", label: "Patient name A–Z" },
];

export const DEFAULT_QUEUE_FILTERS: QueueFilters = {
  q: "",
  status: "todo",
  reviewer: "",
  forWhom: "",
  from: "",
  to: "",
  sort: "oldest", // a queue is worked first come, first served
  perPage: 25,
};

const STATUS_CODES: Record<QueueStatus, string[]> = {
  todo: ["pending_review", "in_review"],
  pending_review: ["pending_review"],
  in_review: ["in_review"],
  digitized: ["digitized"],
  rejected: ["rejected"],
  all: ["pending_review", "in_review", "digitized", "rejected"],
};

/** The API query for the filters (without the page), or null when the date range is backwards. */
export function toQueueQuery(f: QueueFilters): Omit<UploadQueueQuery, "page"> | null {
  const range = dateRange(f.from, f.to);
  if (range === null) return null;
  return {
    status: STATUS_CODES[f.status],
    q: f.q.trim() || undefined,
    reviewer: f.reviewer || undefined,
    for_whom: f.forWhom || undefined,
    uploaded_from: range.created_from,
    uploaded_to: range.created_to,
    sort: f.sort === "patient" ? "patient_name" : "created_at",
    descending: f.sort === "newest",
    per_page: f.perPage,
  };
}

// The filters in the URL (?q=&status=…), so the queue a page was opened from can be gone back to,
// and walked with Previous / Next from an upload's page. Defaults are left out.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const pick = <T extends string>(value: string | null, options: { value: T }[], fallback: T): T =>
  options.find((o) => o.value === value)?.value ?? fallback;

/** The filters a queue URL's search params hold (anything unknown reads as the default). */
export function queueFiltersFromParams(params: URLSearchParams): QueueFilters {
  const date = (key: string) => (DATE_RE.test(params.get(key) ?? "") ? params.get(key)! : "");
  const perPage = Number(params.get("size"));
  return {
    q: (params.get("q") ?? "").trim(),
    status: pick(params.get("status"), STATUS_OPTIONS, DEFAULT_QUEUE_FILTERS.status),
    reviewer: pick(params.get("reviewer"), REVIEWER_OPTIONS, DEFAULT_QUEUE_FILTERS.reviewer),
    forWhom: pick(params.get("for"), FOR_OPTIONS, DEFAULT_QUEUE_FILTERS.forWhom),
    from: date("from"),
    to: date("to"),
    sort: pick(params.get("sort"), SORT_OPTIONS, DEFAULT_QUEUE_FILTERS.sort),
    perPage: (PER_PAGE_OPTIONS as readonly number[]).includes(perPage) ? perPage : DEFAULT_QUEUE_FILTERS.perPage,
  };
}

/** The URL params for (a change to) the filters; a default value removes its param. */
export function queueFiltersToParams(f: Partial<QueueFilters>): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  const put = <K extends keyof QueueFilters>(key: K, param: string) => {
    if (!(key in f)) return;
    const value = f[key];
    out[param] = value === DEFAULT_QUEUE_FILTERS[key] || value === "" ? null : String(value);
  };
  put("q", "q");
  put("status", "status");
  put("reviewer", "reviewer");
  put("forWhom", "for");
  put("from", "from");
  put("to", "to");
  put("sort", "sort");
  put("perPage", "size");
  return out;
}

/** How many filters narrow the queue beyond its default "to do" view. */
export function activeQueueFilterCount(f: QueueFilters): number {
  return [f.q.trim(), f.status !== "todo", f.reviewer, f.forWhom, f.from || f.to].filter(Boolean).length;
}

/** The filters with every narrowing filter cleared; sort and page size are kept. */
export function clearedQueueFilters(f: QueueFilters): QueueFilters {
  return { ...DEFAULT_QUEUE_FILTERS, sort: f.sort, perPage: f.perPage };
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Time since `iso`: "just now", "12 min ago", "3 hr ago", "2 days ago"; past 30 days, the date. */
export function timeAgo(iso: string, now: number): string {
  const ms = Math.max(0, now - new Date(iso).getTime());
  if (ms < MINUTE) return "just now";
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)} min ago`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)} hr ago`;
  const days = Math.floor(ms / DAY);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** How long an upload still to be done has been waiting: under a day, a day or more, 3+ days. */
export function waitLevel(iso: string, now: number): "recent" | "day" | "long" {
  const ms = now - new Date(iso).getTime();
  return ms >= 3 * DAY ? "long" : ms >= DAY ? "day" : "recent";
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** A person's details for a queue row, e.g. "Child · 5 yrs · Female" (whatever is on file). */
export function personDetails(p: UploadPerson): string {
  const parts: string[] = [];
  if (p.relationship) parts.push(capitalise(p.relationship));
  if (p.age !== null) parts.push(p.age < 1 ? "under 1 yr" : `${p.age} yr${p.age === 1 ? "" : "s"}`);
  if (p.gender) parts.push(capitalise(p.gender));
  return parts.join(" · ");
}

/** Where an upload's review stands, in a line: who picked it up or resolved it, and the outcome. */
export function reviewSummary(u: Upload, now: number): string {
  const who = u.reviewed_by_name ?? "a pharmacist";
  const when = u.reviewed_at ? ` · ${timeAgo(u.reviewed_at, now)}` : "";
  switch (u.status) {
    case "pending_review":
      return "Not picked up yet";
    case "in_review":
      return `In review by ${who}${when}`;
    case "digitized":
      return `Digitised${u.digitized_prescription_ref ? ` as ${u.digitized_prescription_ref}` : ""} by ${who}${when}`;
    case "rejected":
      return `Rejected${u.reject_reason ? ` (${u.reject_reason})` : ""} by ${who}${when}`;
    case "cancelled":
      return "Cancelled by the patient";
    default:
      return u.status.replace(/_/g, " ");
  }
}

/** Still to do: waiting to be picked up, or in review. */
export const isTodo = (u: Upload): boolean => u.status === "pending_review" || u.status === "in_review";

/** How the time an upload still to do has waited reads: grey, then amber after a day, red after 3. */
export const WAIT_TONE = { recent: "text-gray-700", day: "text-amber-700", long: "font-medium text-red-700" } as const;

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** A +91 number spaced for reading ("+91 98765 43210"); anything else as it came. */
export function formatPhone(e164: string): string {
  const d = localDigits(e164);
  return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : e164;
}

/** Why an upload can't be digitised (the API's RejectReasonStr), as the pharmacist picks it; the
 *  patient sees the reason and the note. */
export const REJECT_REASONS: { value: string; label: string; hint: string }[] = [
  { value: "illegible", label: "Illegible", hint: "The medicines or doses can't be read." },
  { value: "expired", label: "Expired", hint: "Too old to dispense from." },
  { value: "invalid", label: "Not a valid prescription", hint: "No doctor, signature or registration, or not a prescription." },
  { value: "duplicate", label: "Duplicate", hint: "Already uploaded or digitised." },
  { value: "other", label: "Something else", hint: "Say what in the note." },
];
